import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { colorFor, PRODUCT_NAME, roleAtLeast, type PresenceUser } from '@os/shared';
import { SCHEMA_VERSION, type SceneStore } from '@os/doc';
import { pageLabel } from '../../features/pages/labels';
import { qk, useFavorites, useInbox, useInvalidateContent, useOrg, useToggleFavorite } from '../../features/dashboard/queries';
import { boardPlace } from '../../features/editor/board-place';
import { forgetLocalBoard, type LocalBoard } from '../../features/editor/persistence';
import { createBoardMediaSession, type BoardMediaSession } from '../../features/media/session';
import type { LinkServices } from '../../features/links/services';
import type { LocalMediaStore } from '../../features/media/store';
import { SyncStatus } from '../../features/editor/SyncStatus';
import { SaveIndicator } from '../../features/editor/SaveIndicator';
import { QuotaBanner } from '../../features/editor/QuotaBanner';
import { hasUnsent, useLeaveGuard } from '../../features/editor/useLeaveGuard';
import { ApiError, type BoardAccess } from '../../lib/api';
import { createSyncProvider, type SyncProvider, type SyncProviderOptions } from '../../lib/sync/provider';
import { presenceIn } from '../../lib/sync/mode';
import { useApi } from '../providers';
import { href, Link, navigate, useLocation } from '../router';
import { useAccountCommands } from '../../features/editor/account-commands';
import { RoleChip } from '../../features/members/RoleChip';
import { barOf, displayRole } from '../../features/members/rules';
import { BoardView, download } from './BoardRoute';
import { canRestoreVersions, capabilitiesOf } from '../../features/sharing/capabilities';
import { GuestBar } from '../../features/sharing/GuestBar';
import { ShareButton, ShareDialog } from '../../features/sharing/ShareDialog';
import { HistoryButton } from '../../features/history/HistoryButton';
import { VersionPreview } from '../../features/history/VersionPreview';
import { Toast } from '../../ui/Toast';
import type { BoardVersion } from '../../lib/version-types';
import { Watermark } from '../../features/sharing/Watermark';
import { offlineCopy } from '../../features/export/offline-copy';
import { TopBar } from '../../features/editor/TopBar';
import { TitlePill } from '../../features/editor/TitlePill';
import { useLayout } from '../layout';
import { usePaletteCommands } from '../../features/palette/registry';
import { DuplicateBoardDialog } from '../../features/dashboard/DuplicateBoardDialog';
import { SaveTemplateDialog } from '../../features/templates/SaveTemplateDialog';
import { Badge, linkClass } from '../../ui';
import { BoardSkeleton, ErrorState } from '../../ui/states';

export type ConnectSync = (opts: Pick<SyncProviderOptions, 'boardId' | 'doc' | 'getTicket'>) => SyncProvider;

/**
 * A board of the organization: access checked against the server (unknown and forbidden look the same), view
 * recorded for "Recent", then the local document is connected live to its room (spec §7). The role the room
 * announces drives read-only; an editor's first full sync repairs orphaned parents once.
 */
export function ServerBoardView({ boardId, connect = createSyncProvider }: { boardId: string; connect?: ConnectSync }) {
  const { t } = useTranslation();
  const api = useApi();
  const { search } = useLocation();
  const initialThread = useMemo(() => new URLSearchParams(search).get('thread'), [search]);
  const access = useQuery({ queryKey: qk.board(boardId), queryFn: () => api.getBoard(boardId), retry: false });
  const ready = access.isSuccess;
  const [session, setSession] = useState<{ board: LocalBoard; sync: SyncProvider } | null>(null);
  const connectRef = useRef(connect);
  connectRef.current = connect;
  const initialRole = useRef(access.data?.role);
  initialRole.current = access.data?.role;
  const repaired = useRef<SyncProvider | null>(null);
  const [preview, setPreview] = useState<BoardVersion | null>(null);
  const [restored, setRestored] = useState(false);
  const [sharing, setSharing] = useState(false);
  const profile = useQuery({ queryKey: qk.profile, queryFn: () => api.getProfile() });
  const me = useMemo<PresenceUser | null>(
    () =>
      profile.data
        ? { id: profile.data.id, n: profile.data.displayName || '—', c: profile.data.color ?? colorFor(profile.data.id), ...(profile.data.avatarKey ? { a: profile.data.avatarKey } : {}) }
        : null,
    [profile.data],
  );

  useEffect(() => {
    if (ready) api.touchBoardView(boardId).catch(() => undefined);
  }, [ready, api, boardId]);

  // No access (revoked while away, link expired, removed): nothing of the board stays on this device.
  const gone = access.error instanceof ApiError && access.error.code === 'not_found';
  useEffect(() => {
    if (gone) void forgetLocalBoard(boardId);
  }, [gone, boardId]);

  const meId = useRef<string | null>(null);
  meId.current = profile.data?.id ?? null;
  const prepare = useCallback(
    async (board: LocalBoard) => {
      // Every object this browser adds records its author (P5d).
      board.store.setAuthor(() => meId.current);
      // Read-only from the server's answer until the room says otherwise (archived boards open read-only).
      board.store.setReadOnly(!roleAtLeast(initialRole.current ?? 'none', 'editor'));
      const sync = connectRef.current({ boardId, doc: board.store.doc, getTicket: () => api.getRoomTicket(boardId).then((r) => r.ticket) });
      setSession({ board, sync });
      return () => {
        sync.destroy();
        setSession((s) => (s?.sync === sync ? null : s));
      };
    },
    [api, boardId],
  );

  useEffect(() => {
    if (!session) return;
    const { board, sync } = session;
    const apply = () => {
      const role = sync.role ?? initialRole.current ?? 'none';
      // A room on a newer document schema (P14): this tab's build must not write to it until reloaded.
      const outdated = sync.roomSchema !== null && sync.roomSchema > SCHEMA_VERSION;
      board.store.setReadOnly(sync.status === 'revoked' || outdated || !roleAtLeast(role, 'editor'));
      if (repaired.current !== sync && sync.synced && sync.role && roleAtLeast(sync.role, 'editor')) {
        repaired.current = sync;
        board.store.commands.repairOrphans();
      }
    };
    apply();
    return sync.subscribe(apply);
  }, [session]);

  const links = useCallback(
    (s: BoardMediaSession): LinkServices => ({
      unfurl: (url) => api.unfurl(url),
      thumbnail: async (url) => {
        const blob = await api.unfurlImage(url);
        return s.importAsset(new File([blob], 'preview', { type: blob.type }));
      },
    }),
    [api],
  );
  const queryClient = useQueryClient();
  // The room announces a role the server's access answer does not have (grant changed, link revoked): the
  // flags may have changed too, so ask again. Compared with the answer, not with the previous message, so a
  // message that arrives before this effect subscribes is not missed.
  // A guest or external whose access is revoked keeps nothing: the board's copy on this device is deleted and
  // the access answer, asked again, turns into the not-found screen. Members keep the refused-edits rescue.
  const isMember = access.data?.isMember;
  useEffect(() => {
    if (!session || isMember !== false) return;
    let done = false;
    const check = () => {
      if (done || session.sync.status !== 'revoked') return;
      done = true;
      void session.board.forget().catch(() => undefined);
      void queryClient.invalidateQueries({ queryKey: qk.board(boardId) });
    };
    check();
    return session.sync.subscribe(check);
  }, [session, isMember, queryClient, boardId]);
  // The room's quota mode (P12): presence slows down, then stops; the banner says it in words.
  const live = session?.sync;
  const subscribeLive = useCallback((cb: () => void) => live?.subscribe(cb) ?? (() => undefined), [live]);
  const mode = useSyncExternalStore(subscribeLive, () => live?.mode ?? 'normal');
  const resetAt = useSyncExternalStore(subscribeLive, () => live?.resetAt ?? null);
  const presenceMode = presenceIn(mode);
  const accessRole = access.data?.role;
  useEffect(() => {
    if (!session || !accessRole) return;
    const check = () => {
      const role = session.sync.role;
      if (role && role !== accessRole) void queryClient.invalidateQueries({ queryKey: qk.board(boardId) });
    };
    check();
    return session.sync.subscribe(check);
  }, [session, accessRole, queryClient, boardId]);
  const media = useCallback(
    (board: LocalBoard, local: LocalMediaStore) =>
      createBoardMediaSession({
        boardId, api, store: board.store, local,
        reducedMotion: typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches,
        // The preview is sent as the board closes: the dashboard card may already have cached "none".
        onThumbnail: () => void queryClient.invalidateQueries({ queryKey: ['thumbnail', boardId] }),
      }),
    [api, boardId, queryClient],
  );

  // Unknown and forbidden look the same; any other failure (server error, network) is not "does not exist": the
  // board is kept on this device and the access is asked again (P12a). A failed refresh keeps the open board.
  const denied = access.error instanceof ApiError && (access.error.code === 'not_found' || access.error.code === 'forbidden');
  if (access.isError && !denied && !access.data) {
    return (
      <div className="flex h-full items-center">
        <ErrorState title={t('board.error.unknown')} onRetry={() => void access.refetch()} retryLabel={t('dashboard.retry')}>
          <Link to={href('home')} className={linkClass}>
            ← {t('dashboard.home')}
          </Link>
        </ErrorState>
      </div>
    );
  }
  if (denied) {
    return (
      <div className="flex h-full items-center">
        <ErrorState title={t('board.notFound')}>
          <Link to={href('home')} className={linkClass}>
            ← {t('dashboard.home')}
          </Link>
        </ErrorState>
      </div>
    );
  }
  if (!access.data) {
    return <BoardSkeleton label={t('board.loading')} />;
  }
  const data = access.data;
  const caps = capabilitiesOf(data.role, data.flags);
  const name = profile.data?.displayName ?? '';
  // Share opens from the members panel (P16-3), as from the command palette.
  const canShare = data.role === 'manager' && api.capabilities.sharing;
  return (
    <>
      {/* The live board stays connected under a version preview, unreachable until the preview closes. */}
      <div className="h-full" inert={preview ? true : undefined}>
        <BoardView
          boardId={boardId}
          prepare={prepare}
          chrome={(board) =>
            session?.board === board && (
              <BoardBar access={data} store={board.store} sync={session.sync} name={name} onPreview={api.capabilities.versions ? setPreview : undefined} />
            )
          }
          presence={(board) =>
            session?.board === board && me
              ? { awareness: session.sync.awareness, me, people: () => api.boardPeople(boardId), onShare: canShare ? () => setSharing(true) : undefined, economy: presenceMode.economy, off: !presenceMode.on }
              : null
          }
          comments={(board) =>
            api.capabilities.comments && session?.board === board && me
              ? { boardId, me: { id: me.id, name: me.n }, role: data.role, linkOnly: data.linkOnly, sync: session.sync, initialThread }
              : null
          }
          authors={(board) => (session?.board === board && me ? { boardId, me: me.id, meName: me.n, meAvatarKey: profile.data?.avatarKey, resolve: (ids) => api.authors(boardId, ids), people: () => api.mentionables(boardId) } : null)}
          media={media}
          links={links}
          capabilities={caps}
          title={data.board.title}
          remoteHashes={(ids) => api.assetHashes(boardId, ids)}
          home={data.isMember ? { to: href('home'), label: t('mainMenu.dashboard'), confirm: () => (session ? hasUnsent(session.sync) : false) } : null}
          checkAsset={(sha256) => api.checkAsset(boardId, sha256)}
          figma={data.isMember && api.capabilities.figma ? api : undefined}
          overlay={caps.protected ? <Watermark text={`${PRODUCT_NAME} · ${name}`} /> : null}
        />
      </div>
      {preview && (
        <VersionPreview
          boardId={boardId}
          version={preview}
          canRestore={canRestoreVersions(data)}
          onClose={() => setPreview(null)}
          onRestored={() => {
            setPreview(null);
            setRestored(true);
          }}
        />
      )}
      {session && !preview && <QuotaBanner mode={mode} resetAt={resetAt} />}
      {restored && <Toast message={t('history.restored')} onDone={() => setRestored(false)} />}
      {sharing && <ShareDialog scope={{ type: 'board', id: boardId }} title={data.board.title} page={openPage(session?.board.store, t)} onClose={() => setSharing(false)} />}
    </>
  );
}

/** Way back to the project (members) or the guest bar (everyone else), board title and live sync state. */
function BoardBar({
  access, store, sync, name, onPreview,
}: { access: BoardAccess; store: SceneStore; sync: SyncProvider; name: string; onPreview?: (v: BoardVersion) => void }) {
  const { t } = useTranslation();
  const api = useApi();
  const subscribe = useCallback((cb: () => void) => store.subscribeReadOnly(cb), [store]);
  const readOnly = useSyncExternalStore(subscribe, () => store.readOnly);
  // Phones (P5f, backlog §14.4): Share and History move to the main menu's Board submenu; the pill is compact.
  const layout = useLayout();
  const phone = layout === 'phone';
  // Every way out asks while edits are unsaved, and the tab asks before closing (P16-3).
  const guard = useLeaveGuard(sync);
  const invalidate = useInvalidateContent();
  const { org } = useOrg();
  const favorites = useFavorites();
  const toggleFavorite = useToggleFavorite();
  const [dialog, setDialog] = useState<'duplicate' | 'template' | null>(null);
  const board = access.board;
  // Where the board lives (P17): « Sans projet » is a place of the dashboard, never a project to go back to.
  const inbox = useInbox(access.isMember ? org?.id : undefined);
  const place = boardPlace({ project: access.project, inboxId: inbox.data, inboxPending: inbox.isPending, noProjectLabel: t('dashboard.noProject') });
  const copies = access.isMember && api.capabilities.boardCopy;
  // The main menu's File entries for members (same ids as the dashboard's actions).
  usePaletteCommands(() => [
    // The way back, at the top of the main menu's File (P5f, backlog §14.2): members only (a guest has no project).
    ...(access.isMember
      ? [
          ...(place?.back
            ? [{ id: 'back', label: t('mainMenu.backTo', { name: place.label }), group: 'board' as const, run: () => guard.confirmLeave(() => navigate(place.to)) }]
            : []),
          { id: 'dashboard', label: t('mainMenu.dashboard'), group: 'board' as const, run: () => guard.confirmLeave(() => navigate(href('home'))) },
        ]
      : []),
    ...(copies && org ? [{ id: 'duplicateBoard', label: t('mainMenu.duplicateBoard'), group: 'board' as const, run: () => setDialog('duplicate') }] : []),
    ...(copies && !board.isTemplate ? [{ id: 'saveTemplate', label: t('mainMenu.saveTemplate'), group: 'board' as const, run: () => setDialog('template') }] : []),
  ]);
  const exportJson = () => {
    const copy = offlineCopy(store.doc, board.title);
    download(copy.name, copy.data);
  };
  const favorite = favorites.data?.some((b) => b.id === board.id) ?? false;
  // The account moved from the top bar to the main menu (P16-3); a guest's link session has no account (P5e).
  useAccountCommands(access.isMember, (to) => guard.confirmLeave(() => navigate(to)));
  const bar = barOf({ role: access.role, isMember: access.isMember, canHistory: !!onPreview, layout, sharing: api.capabilities.sharing });
  return (
    <>
    <TopBar
      left={
        access.isMember ? (
          <TitlePill
            compact={phone}
            title={board.title}
            onRename={
              readOnly
                ? undefined
                : async (title) => {
                    await api.updateBoard(board.id, { title });
                    await invalidate();
                  }
            }
            badge={board.isTemplate ? <Badge tone="accent">{t('boardTemplates.badge')}</Badge> : null}
            favorite={{ on: favorite, onToggle: () => toggleFavorite.mutate({ board, on: !favorite }) }}
            folder={
              place === null ? undefined : {
                label: place.label,
                to: place.to,
                onClick: (e) => {
                  if (!hasUnsent(sync)) return;
                  e.preventDefault();
                  guard.confirmLeave(() => navigate(place.to));
                },
              }
            }
          >
            <SaveIndicator sync={sync} readOnly={readOnly} compact={phone} />
            <SyncStatus provider={sync} onExport={capabilitiesOf(access.role, access.flags).canExport ? exportJson : undefined} />
          </TitlePill>
        ) : (
          <>
            <GuestBar title={access.board.title} name={name} />
            <SaveIndicator sync={sync} readOnly={readOnly} compact={phone} />
            <SyncStatus provider={sync} onExport={capabilitiesOf(access.role, access.flags).canExport ? exportJson : undefined} />
          </>
        )
      }
      lead={bar.roleChip ? <RoleChip role={displayRole({ role: access.role, orgRole: access.isMember ? (org?.role ?? null) : null, linkOnly: access.linkOnly, anonymous: false })} /> : null}
      right={
        <>
          {/* Share lives in the members panel, File and Cmd/Ctrl+Shift+S (P16-3): no button here, only its dialog. */}
          <ShareButton scope={{ type: 'board', id: access.board.id }} title={access.board.title} role={access.role} bare />
          {onPreview && <HistoryButton boardId={access.board.id} access={access} onPreview={onPreview} bare={!bar.history} />}
        </>
      }
    />
    {guard.dialog}
    {dialog === 'duplicate' && org && <DuplicateBoardDialog board={board} org={org} onClose={() => setDialog(null)} />}
    {dialog === 'template' && <SaveTemplateDialog board={board} onClose={() => setDialog(null)} />}
    </>
  );
}

/** The page open in the editor when the board has several (P14), for « Copier le lien vers cette page ». */
function openPage(store: SceneStore | undefined, t: (key: string, opts?: Record<string, unknown>) => string): { id: string; name: string } | undefined {
  const pages = store?.pages() ?? [];
  if (!store || pages.length < 2) return undefined;
  const i = pages.findIndex((p) => p.id === store.page);
  return { id: store.page, name: pageLabel(t, pages[i]!, i) };
}
